import React, { FC, useMemo, useState } from 'react';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import { IconButton } from '@eika/button';
import Card from '../../../../../common/Card/Card';
import FundRiskClassIndicator from '../FundRiskClassIndicator';
import { SelectableProfile, StorebrandContractFund } from '../../../../../domain/Storebrand';
import './SavingsProfileInfo.scss';
import { DownscalingModal } from '../DownscalingModal';
import { formatAmount } from '../../../../../common/util/Functions';
import SelectableProfileFundAllocation from '../switch/SelectableProfileFundAllocation';
import { useTranslate } from '../../../../../hooks/useTranslate';
import {CostExplanationModal} from "../CostExplanationModal";

type SavingProfileInfoProps = {
    customerFund?: StorebrandContractFund;
    selectableProfile?: SelectableProfile;
    riskReductionEnabled?: boolean;
    isReductionTarget?: boolean;
    owned: boolean;
};
export const SavingProfileInfo: FC<SavingProfileInfoProps> = ({
    customerFund,
    selectableProfile,
    riskReductionEnabled,
    isReductionTarget,
    owned,
}) => {
    const [modalOpen, setModalOpen] = useState<'downscaling' | 'costExplanation' | false>(false);

    const translate = useTranslate();

    const data = useMemo(
        () => ({
            productLink: customerFund?.fundSheetURL || selectableProfile?.productSheetLink,
            riskClass: customerFund?.riskClass || selectableProfile?.riskClass,
            riskLevel: customerFund?.riskLevel || selectableProfile?.riskLevel,
            cost: customerFund?.fundManagementFee || selectableProfile?.fundManagementFee,
            balance: customerFund?.marketValue,
            fundAllocationPercentage: selectableProfile?.fundAllocationPercentage,
        }),
        [customerFund, selectableProfile]
    );

    const downscaling = useMemo(() => {
        switch (true) {
            case !owned && !data.balance:
                return undefined;
            case !riskReductionEnabled:
                return <span className="font-normal">{translate('youDontHaveAutomaticDownscaling')}</span>;
            case riskReductionEnabled && isReductionTarget:
                return <span className="font-normal">{translate('profileDownscaledTo')}</span>;
            case riskReductionEnabled:
                return <span className="font-normal">{translate('youHaveAutomaticDownscaling')}</span>;
            default:
                return undefined;
        }
    }, [isReductionTarget, riskReductionEnabled, owned]);

    return (
        <>
            <div className="savings-profile-info">
                <div>
                    {downscaling && (
                        <Card
                            data={{
                                textList: [
                                    [
                                        {
                                            element: (
                                                <div className="downscaling-row">
                                                    {downscaling}
                                                    <IconButton
                                                        type="button"
                                                        onClick={() => {
                                                            setModalOpen('downscaling');
                                                        }}
                                                        icon={IconTypes.INFO_24}
                                                    />
                                                </div>
                                            ),
                                        },
                                    ],
                                ],
                            }}
                            type="list-item"
                        />
                    )}
                    {data.balance && (
                        <Card
                            data={{
                                textList: [
                                    [
                                        { text: translate('totalInventory'), type: 'font-normal' },
                                        { text: formatAmount(data.balance, true), type: '' },
                                    ],
                                ],
                            }}
                            type="list-item"
                        />
                    )}
                    {!!data.fundAllocationPercentage && (
                        <Card
                            data={{
                                textList: [
                                    [
                                        { text: translate('fundType'), type: 'font-normal' },
                                        {
                                            element: (
                                                <SelectableProfileFundAllocation
                                                    fundAllocationPercentage={data.fundAllocationPercentage}
                                                />
                                            ),
                                        },
                                    ],
                                ],
                            }}
                            type="list-item"
                        />
                    )}
                    {data.cost && (
                        <Card
                            data={{
                                textList: [
                                    [
                                        { text: translate('cost'), type: 'font-normal' },
                                        {
                                            element: (
                                                <div className="-with-explanation">
                                                    <span>{`${formatAmount(data.cost, false)} %`}</span>
                                                    <IconButton
                                                        type="button"
                                                        onClick={() => {
                                                            setModalOpen('costExplanation');
                                                        }}
                                                        icon={IconTypes.INFO_24}
                                                    />
                                                </div>
                                            ),
                                        },
                                    ],
                                ],
                            }}
                            type="list-item"
                        />
                    )}
                    {data.riskClass && (
                        <Card
                            data={{
                                textList: [
                                    [
                                        {
                                            element: (
                                                <div className="risk-level-info-wrapper">
                                                    <div>{translate('riskLevel')}</div>
                                                    <div>
                                                        {data.riskLevel && data.riskLevel !== 'UNKNOWN' && (
                                                            <span>{translate(data.riskLevel.toLowerCase())}</span>
                                                        )}
                                                        <FundRiskClassIndicator riskClass={data.riskClass} verbose />
                                                    </div>
                                                </div>
                                            ),
                                        },
                                    ],
                                ],
                            }}
                            type="list-item"
                        />
                    )}
                </div>

                {data.productLink && (
                    <Link href={data.productLink} icon={IconTypes.EKSTERN_24} iconOnRightSide>
                        {translate('openProductLink')}
                    </Link>
                )}
            </div>
            <DownscalingModal onClose={() => setModalOpen(false)} open={modalOpen === 'downscaling'} />
            <CostExplanationModal onClose={() => setModalOpen(false)} open={modalOpen === 'costExplanation'} />
        </>
    );
};
